// api.js — every backend call the control center makes, in one place.
//
// Two surfaces are in play and the split is deliberate:
//   /admin/*  — added for this app: cross-org reads, support actions.
//   /orgs/*   — the pre-existing tenant admin surface. Creating an org and
//               provisioning a user already live there and are already
//               superuser/org-admin gated, so they are reused rather than
//               duplicated under /admin.
//
// Errors: the backend answers `{"detail": {"code", "message"}}` on every
// failure path. `ApiError` carries both so screens can show the message the
// backend wrote instead of inventing one.

import { API_BASE } from './config';
export { errorText } from './errorText';
import { authFetch } from './auth';
import { FLAP_SLAB_ROUTES } from './flapSlabs';
import { CARTON_ROUTES } from './cartons';

export class ApiError extends Error {
  constructor(status, code, message, body) {
    super(message || code || `http_${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code || null;
    this.body = body || null;
  }
}

/** Drop null/undefined/'' /false so a cleared filter disappears from the URL
 *  rather than being sent as an empty value the backend would try to match on.
 *  Dropping `false` is deliberate: every boolean param here narrows the result,
 *  so false IS the default and never needs sending. */
function qs(params = {}) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '' || v === false) return;
    sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** Multipart POST. Deliberately does NOT set Content-Type: the browser has to
 *  write it itself so the multipart boundary matches the body it generates. */
async function requestForm(path, formData, { method = 'POST' } = {}) {
  const resp = await authFetch(`${API_BASE}${path}`, { method, body: formData });
  let payload = null;
  try { payload = await resp.json(); } catch { /* non-JSON */ }
  if (!resp.ok) {
    const detail = payload?.detail;
    throw new ApiError(resp.status,
                       typeof detail === 'object' ? detail?.code : null,
                       typeof detail === 'object' ? detail?.message : null,
                       payload);
  }
  return payload;
}

async function request(path, { method = 'GET', body, signal } = {}) {
  const opts = { method, signal };
  if (body !== undefined) {
    opts.headers = { 'Content-Type': 'application/json' };
    opts.body = JSON.stringify(body);
  }
  const resp = await authFetch(`${API_BASE}${path}`, opts);
  let payload = null;
  try { payload = await resp.json(); } catch { /* 204 or non-JSON */ }
  if (!resp.ok) {
    const detail = payload?.detail;
    throw new ApiError(resp.status,
                       typeof detail === 'object' ? detail?.code : null,
                       typeof detail === 'object' ? detail?.message
                                                  : (typeof detail === 'string' ? detail : null),
                       payload);
  }
  return payload;
}

/** A file, as a Blob, through the same signed-in fetch — an <a href> cannot
 *  carry the Authorization header. Errors arrive as JSON like every other. */
async function requestBlob(path) {
  const resp = await authFetch(`${API_BASE}${path}`);
  if (!resp.ok) {
    let payload = null;
    try { payload = await resp.json(); } catch { /* non-JSON */ }
    const detail = payload?.detail;
    throw new ApiError(resp.status, typeof detail === 'object' ? detail?.code : null,
                       typeof detail === 'object' ? detail?.message : null, payload);
  }
  return resp.blob();
}

/* ── platform ─────────────────────────────────────────────────────── */

export const api = {
  /** Unauthenticated deploy probe — the one call that still answers when the
   *  session is dead, which is exactly when you want to know if the API is up. */
  health: () => fetch(`${API_BASE}/health`).then((r) => r.json()),

  overview: (windowH = 24) => request(`/admin/overview${qs({ window_h: windowH })}`),
  activity: (days = 14, orgId = null) => request(`/admin/activity${qs({ days, org_id: orgId })}`),

  /* ── orgs ───────────────────────────────────────────────────────── */
  orgs: ({ q, status, windowD } = {}) =>
    request(`/admin/orgs${qs({ q, status, window_d: windowD })}`),
  org: (orgId, { windowD } = {}) =>
    request(`/admin/orgs/${encodeURIComponent(orgId)}${qs({ window_d: windowD })}`),
  patchOrg: (orgId, patch) =>
    request(`/admin/orgs/${encodeURIComponent(orgId)}`, { method: 'PATCH', body: patch }),
  createOrg: (name) => request('/orgs', { method: 'POST', body: { name } }),
  uploadOrgLogo: (orgId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return requestForm(`/admin/orgs/${encodeURIComponent(orgId)}/logo`, fd);
  },
  deleteOrgLogo: (orgId) =>
    request(`/admin/orgs/${encodeURIComponent(orgId)}/logo`, { method: 'DELETE' }),

  /* ── users ──────────────────────────────────────────────────────── */
  users: ({ q, orgId, role, status, limit = 50, offset = 0 } = {}) =>
    request(`/admin/users${qs({ q, org_id: orgId, role, status, limit, offset })}`),
  user: (userId, { windowD } = {}) =>
    request(`/admin/users/${encodeURIComponent(userId)}${qs({ window_d: windowD })}`),
  provisionUser: (orgId, { email, role, display_name }) =>
    request(`/orgs/${encodeURIComponent(orgId)}/users`,
            { method: 'POST', body: { email, role, display_name: display_name || '' } }),
  patchUser: (orgId, userId, patch) =>
    request(`/orgs/${encodeURIComponent(orgId)}/users/${encodeURIComponent(userId)}`,
            { method: 'PATCH', body: patch }),
  /** #73 — send the activation email again. Answers { ...user, activation_email }. */
  resendActivationEmail: (orgId, userId) =>
    request(`/orgs/${encodeURIComponent(orgId)}/users/${encodeURIComponent(userId)}/activation-email`,
            { method: 'POST' }),
  signOutUser: (userId) =>
    request(`/admin/users/${encodeURIComponent(userId)}/sign-out`, { method: 'POST' }),
  unbindSso: (userId) =>
    request(`/admin/users/${encodeURIComponent(userId)}/unbind-sso`, { method: 'POST' }),
  /** member | superuser | god. God-only on the backend; the UI hides it otherwise. */
  setAccessLevel: (userId, level) =>
    request(`/admin/users/${encodeURIComponent(userId)}/access`,
            { method: 'PATCH', body: { level } }),

  /* ── monitoring ─────────────────────────────────────────────────── */
  jobs: ({ status, kind, orgId, userId, openOnly, limit = 50, offset = 0 } = {}) =>
    request(`/admin/jobs${qs({ status, kind, org_id: orgId, user_id: userId,
                               open_only: openOnly, limit, offset })}`),
  cancelJob: (jobId) =>
    request(`/jobs/${encodeURIComponent(jobId)}/cancel`, { method: 'POST' }),
  events: ({ orgId, actorUserId, eventType, entityType, entityId, requestId,
             since, until, limit = 50, offset = 0 } = {}) =>
    request(`/admin/events${qs({ org_id: orgId, actor_user_id: actorUserId,
                                 event_type: eventType, entity_type: entityType,
                                 entity_id: entityId, request_id: requestId,
                                 since, until, limit, offset })}`),
  eventFacets: (windowD = 7) => request(`/admin/events/facets${qs({ window_d: windowD })}`),

  /* ── engine: capacity + per-org tuning (K6/K7/K8) ───────────────── */
  /** Per-org queue depth and windowed load. AUTHENTICATED on purpose — the
   *  open /health keeps counts and ages with no tenant data, and a per-tenant
   *  breakdown on that URL would undo the boundary D3 set. */
  queue: (windowD = 7) => request(`/admin/queue${qs({ window_d: windowD })}`),
  orgEngineConfig: (orgId) =>
    request(`/admin/orgs/${encodeURIComponent(orgId)}/engine-config`),
  /** `null` for a key CLEARS it, so the chain falls back to the shop tier.
   *  Omitting a key leaves it alone. An override with no way back is one
   *  nobody dares set. */
  patchOrgEngineConfig: (orgId, patch) =>
    request(`/admin/orgs/${encodeURIComponent(orgId)}/engine-config`,
            { method: 'PATCH', body: patch }),
  /** The prepress ranking cache of the backend process that answers — the
   *  only cache holding a decision. Clearing is for every org; the response's
   *  `cleared` is exact, `stats_after` only a snapshot. */
  rankingCache: () => request('/admin/engine/ranking-cache'),
  clearRankingCache: () =>
    request('/admin/engine/ranking-cache/clear', { method: 'POST' }),

  /* ── flap slabs (V2 #54) ────────────────────────────────────────── */
  /** The platform's flap-slab table, its version, and the engine's built-in
   *  slabs — the ONLY place this app gets slab numbers from. */
  flapSlabs: () => request(...FLAP_SLAB_ROUTES.load()),
  /** Replace the table whole (`null` = back to the built-in slabs).
   *  `expectedVersion` is the version this editor loaded: a stale save is
   *  refused with 409 flap_slabs_conflict instead of overwriting. The request
   *  shapes live in flapSlabs.js, where node can test them. */
  saveFlapSlabs: (table, expectedVersion) =>
    request(...FLAP_SLAB_ROUTES.save(table, expectedVersion)),
  flapSlabVersions: (limit = 20) => request(...FLAP_SLAB_ROUTES.versions(limit)),

  /* ── cartons (V2 #100) ──────────────────────────────────────────── */
  /** A page of cartons in this admin's Control Center view. Send `view` only
   *  when the admin has just chosen one: the backend stores and records it,
   *  and answers a request with none with the remembered view (cartons.js). */
  cartons: (args) => request(...CARTON_ROUTES.browse(args)),
  /** unshare | delete | restore, with the reason the backend requires and
   *  records (#99). Answers the carton as it now is. */
  cartonAction: (cartonId, action, reason) =>
    request(...CARTON_ROUTES.act(cartonId, action, reason)),
  /** A live carton's file: 'source' (the PDF or artwork) or 'die'. */
  cartonFile: (cartonId, which) =>
    requestBlob(`/library/cartons/${encodeURIComponent(cartonId)}/${which === 'die' ? 'die' : 'source'}`),

  /* ── support ────────────────────────────────────────────────────── */
  supportLookup: (email, { windowD } = {}) =>
    request(`/admin/support/lookup${qs({ email, window_d: windowD })}`),
};

/** The backend returns logo URLs as API-relative paths (they are also used by
 *  the main app), so the control center prefixes its own base to render them. */
export function assetUrl(path) {
  return path ? `${API_BASE}${path}` : null;
}
